import React, { Fragment, useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { NumericFormat, PatternFormat } from "react-number-format";
import {
  useEbillingDispatch,
  useEbillingState,
} from "../../Context/ebilling/context";
import {
  requestListKap,
  requestCreateEbillingMass,
  resetNewEbilling,
  requestListEbillingUsage,
  clearEbillingInitialData,
} from "../../Context/ebilling/actions";

import "./style.css";
import { useTranslation } from "react-i18next";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from "@headlessui/react";
import {
  ChevronDownIcon,
  TrashIcon,
  CogIcon,
  ExternalLinkIcon,
} from "@heroicons/react/outline";
import {
  addLocalNotification,
  clearSendDraftStatus,
  requestGetConsultation,
  requestListBusiness,
  sendMessage,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  useConsultationDispatch,
  useConsultationState,
  useNotificationDispatch,
} from "../../Context";
import { Button } from "../../Components/Button";
import { useLocation, useNavigate } from "react-router-dom";
import { ModalConfirm } from "../../Components/ModalConfirm";
import { CountBadge } from "../../Components/CountBadge";
import Badge from "../../Components/Badge";
import Partner from "../../Components/Partner";
import { Modal } from "../../Components/Modal";
import { EBILLING_TERMS } from "../../Constants";
import FormLabel from "../../Components/FormLabel";
import { generateArrayOfYears, getApiErrorBody } from "../../Utils";
import { Alert } from "../../Components/Alert";
import ErrorSelect from "../../Components/ErrorSelect";

const configItems = [
  { id: "kapKjs", name: "Same KAP & KJS" },
  { id: "npwp", name: "Same NPWP & Email" },
  { id: "total", name: "Same Total" },
  { id: "period", name: "Same Tax Period" },
];

const dailyQuota = 3;

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

const MONTHS = [
  { label: "January", value: 1 },
  { label: "February", value: 2 },
  { label: "March", value: 3 },
  { label: "April", value: 4 },
  { label: "May", value: 5 },
  { label: "June", value: 6 },
  { label: "July", value: 7 },
  { label: "August", value: 8 },
  { label: "September", value: 9 },
  { label: "October", value: 10 },
  { label: "November", value: 11 },
  { label: "December", value: 12 },
];

// Temporary limit for ctas
const years = generateArrayOfYears(2024);

const EbillingCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const consultationDispatch = useConsultationDispatch();

  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const kapIdParam = searchParams.get("kap");
  const kjsIdParam = searchParams.get("kjs");
  const consultationIdParam = searchParams.get("consultation_id");

  const translatedMonths = MONTHS.map((month) => ({
    ...month,
    label: t(month.label),
  }));

  // Context
  const dispatch = useEbillingDispatch();
  const notificationDispatch = useNotificationDispatch();
  const {
    kapItems,
    newEbilling,
    newEbillingMass,
    initialData,
    usage,
    loading,
    error,
  } = useEbillingState();
  const { user } = useAuthState();
  const { consultation, sendError, sendDraftStatus, sendLoading } =
    useConsultationState();
  const businessDispatch = useBusinessTransactionDispatch();
  const {
    businesses,
    loading: businessLoading,
    error: businessError,
  } = useBusinessTransactionState();

  // State
  const [rowCount, setrowCount] = useState(1);
  const [formRows, setFormRows] = useState([{ id: 1, rowOpen: true }]);
  const [ebillings, setebillings] = useState({
    1: { year: years[0] },
  });
  const [config, setconfig] = useState({
    kapKjs: true,
    npwp: true,
    total: false,
    period: false,
  });
  const [checkedConfig, setcheckedConfig] = useState(2);
  const [inputErrors, setinputErrors] = useState({});
  const [modal, setModal] = useState({
    visible: false,
    title: "",
    description: "",
    type: "",
  });
  const [quotalLeft, setquotalLeft] = useState(0);
  const [termsAgree, settermsAgree] = useState(true);
  const [openModalTerms, setopenModalTerms] = useState(false);
  const [selectedBusinessError, setselectedBusinessError] = useState({});

  const memoizeFormRows = useMemo(() => {
    return {
      formRows: formRows.map((row) => ({
        id: row.id,
        taxNumber:
          ebillings[row.id]?.selectedBusiness?.taxNumber ||
          ebillings[row.id]?.npwp?.value,
      })),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    user.taxpayerInfo?.npwp,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    JSON.stringify(formRows.map((row) => ebillings[row.id]?.selectedBusiness)),
  ]);

  useEffect(() => {
    if (user.id) {
      requestListBusiness(businessDispatch, { owner: user.id });
    }
  }, [businessDispatch, user.id]);

  useEffect(() => {
    const updatedEbillings = { ...ebillings };
    const defaultNpwp = user.taxpayerInfo?.npwp;
    const updatedErrors = { ...selectedBusinessError };

    formRows.forEach(({ id }) => {
      const currentEbilling = ebillings[id];

      const currentBusiness = currentEbilling?.selectedBusiness;
      const currentBusinessTaxNumber = currentBusiness?.taxNumber;
      const currentNpwp = currentEbilling?.npwp?.value;
      const memoizedRow = memoizeFormRows.formRows.find((row) => row.id === id);

      let newNpwpValue =
        memoizedRow?.taxNumber || currentBusinessTaxNumber || defaultNpwp;

      if (currentBusiness) {
        if (!currentBusinessTaxNumber) {
          newNpwpValue = "";
          updatedErrors[id] = "businessDoesNotHaveTaxNumber";
        } else {
          delete updatedErrors[id];
        }
      } else {
        delete updatedErrors[id];
      }

      if (currentNpwp !== newNpwpValue) {
        updatedEbillings[id] = {
          ...currentEbilling,
          npwp: { value: newNpwpValue },
        };
      }
    });

    // Function to perform deep comparison
    const deepEqual = (obj1, obj2) =>
      JSON.stringify(obj1) === JSON.stringify(obj2);

    if (!deepEqual(ebillings, updatedEbillings)) {
      setebillings(updatedEbillings);
    }

    if (!deepEqual(selectedBusinessError, updatedErrors)) {
      setselectedBusinessError(updatedErrors);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoizeFormRows]);

  useEffect(() => {
    if (consultationIdParam && user.membership === "CONSULTANT") {
      requestGetConsultation(
        consultationDispatch,
        {
          id: consultationIdParam,
        },
        true,
      );
    }
  }, [consultationDispatch, consultationIdParam, user.membership]);

  // Pre-select kap and kjs based on params
  useEffect(() => {
    if (kapItems.length > 0 && !ebillings[1].kap?.id && kapIdParam) {
      const preselectKap = kapItems.find((item) => item.value === kapIdParam);
      if (preselectKap) {
        setebillings({
          ...ebillings,
          1: {
            ...ebillings[1],
            kap: preselectKap,
          },
        });
      }
    }
    if (
      ebillings[1].kap?.kjs?.items?.length > 0 &&
      !ebillings[1].kjs?.id &&
      kjsIdParam
    ) {
      const preselectKjs = ebillings[1].kap.kjs.items.find(
        (item) => item.kjsId === kjsIdParam,
      );
      if (preselectKjs) {
        setebillings({
          ...ebillings,
          1: {
            ...ebillings[1],
            kjs: {
              ...preselectKjs,
              label: `${preselectKjs.kjsId} - ${preselectKjs.name}`,
              value: preselectKjs.id,
            },
          },
        });
      }
    }
  }, [kapItems, kapIdParam, kjsIdParam, ebillings]);

  // Pre-fill from initialData & user
  useEffect(() => {
    const updatedForm = {};
    // Pre-fill name
    if (initialData?.name) {
      updatedForm.name = initialData.name;
    } else if (user.name && ebillings[1].name === undefined) {
      updatedForm.name = consultation?.id
        ? consultation?.description?.name
        : user.name;
    }
    // Pre-fill email
    if (initialData?.email) {
      updatedForm.email = initialData.email;
    } else if (user.email && ebillings[1].email === undefined) {
      updatedForm.email = consultation?.id
        ? consultation?.description?.email
        : user.email;
    }
    // Pre-fill npwp
    if (initialData?.npwp) {
      updatedForm.npwp = { value: initialData.npwp };
    } else if (
      user.taxpayerInfo?.npwp &&
      ebillings[1].npwp?.value === undefined
    ) {
      updatedForm.npwp = {
        value: consultation?.id ? "" : user.taxpayerInfo.npwp,
      };
    }
    // Pre-fill startPeriod endPeriod
    if (initialData?.startPeriod) {
      const startPeriodValue = parseInt(initialData.startPeriod, 10);
      const selectedMonth = translatedMonths.find(
        (month) => month.value === startPeriodValue,
      );
      updatedForm.startPeriod = {
        value: selectedMonth ? selectedMonth.value : "",
        label: selectedMonth ? selectedMonth.label : "",
      };
    }
    if (initialData?.endPeriod) {
      const endPeriodValue = parseInt(initialData.endPeriod, 10);
      const selectedMonth = translatedMonths.find(
        (month) => month.value === endPeriodValue,
      );
      updatedForm.endPeriod = {
        value: selectedMonth ? selectedMonth.value : "",
        label: selectedMonth ? selectedMonth.label : "",
      };
    }
    // Pre-fill year
    if (initialData?.year) {
      updatedForm.year = {
        value: initialData.year,
        label: initialData.year,
      };
    }
    // Pre-fill total
    if (initialData?.total) {
      updatedForm.total = {
        value: initialData.total,
      };
    }
    if (initialData?.sk || initialData?.skNumber) {
      updatedForm.sk = {
        value: initialData.sk || initialData?.skNumber,
      };
    }
    if (initialData?.note) {
      updatedForm.note = initialData.note;
    }
    if (initialData?.collectorNpwp) {
      updatedForm.otherNpwp = { value: initialData.collectorNpwp };
      updatedForm.taxSubject = "other";
    }
    if (initialData?.nop) {
      updatedForm.nop = { value: initialData.nop };
    }

    if (initialData?.businessID) {
      const selectedBusiness = businesses.find(
        (business) => business.id === initialData.businessID,
      );
      if (selectedBusiness) {
        updatedForm.selectedBusiness = selectedBusiness;
      }
    }

    // Pre-fill form
    if (Object.keys(updatedForm).length > 0) {
      setebillings({
        ...ebillings,
        1: {
          ...ebillings[1],
          ...updatedForm,
        },
      });
      clearEbillingInitialData(dispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, ebillings[1], businesses]);

  useEffect(() => {
    // List initial data
    if (user.id) {
      requestListKap(dispatch);
      if (user.membership === "FREE") {
        requestListEbillingUsage(dispatch, { userID: user.id });
      }
    }

    return () => {
      resetNewEbilling(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.id]);

  useEffect(() => {
    setquotalLeft(dailyQuota - usage);
  }, [usage]);

  useEffect(() => {
    let checkedCount = 0;
    Object.keys(config).forEach((configKey) => {
      if (config[configKey] === true) {
        checkedCount += 1;
      }
    });
    setcheckedConfig(checkedCount);
  }, [config]);

  useEffect(() => {
    if (!error) {
    } else {
      let errorMessage = "Error creating ebilling.";
      const errorResponseBody = getApiErrorBody(error);
      if (errorResponseBody?.errors) {
        errorMessage = "";
        for (let i = 0; i < errorResponseBody.errors.length; i++) {
          if (errorMessage !== "") {
            errorMessage += ` ${errorResponseBody.errors[i]}`;
          } else {
            errorMessage += errorResponseBody.errors[i];
          }
        }
      }
      setModal({
        visible: true,
        title: t("Error"),
        description: t(errorMessage),
        type: "ALERT",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    if (sendError && user.membership === "CONSULTANT") {
      let message = sendError?.message || "Error sending message.";

      addLocalNotification(notificationDispatch, {
        id: `sendError-${Math.floor(Date.now() / 1000)}`,
        type: "error",
        title: "Error",
        body: message,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendError, user.membership]);

  useEffect(() => {
    if (newEbilling.id || newEbillingMass.length > 0) {
      const isMassCreation = newEbillingMass.length > 1;
      setModal({
        visible: true,
        title: t("Success"),
        description: isMassCreation
          ? `${formRows.length} E-Billing ${t("created successfully.")}`
          : `E-Billing ${t("created successfully.")}`,
        type: "SUCCESS",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newEbilling.id, newEbillingMass]);

  useEffect(() => {
    if (consultation.id && sendDraftStatus === "SUCCESS") {
      navigate(`/consultation/${consultation.id}`);
      clearSendDraftStatus(consultationDispatch);
    }
  });

  const navigateToEbilling = () => {
    setModal((prevModal) => ({
      ...prevModal,
      visible: false,
    }));
    if (!error) {
      if (newEbilling.id) {
        navigate(`/ebilling/${newEbilling.id}`);
      } else if (newEbillingMass.length > 0) {
        navigate(
          newEbillingMass.length > 1
            ? "/ebilling"
            : `/ebilling/${newEbillingMass[0]}`,
        );
      }
    }
  };

  const verifyEbillingForm = () => {
    const ebillingIds = Object.keys(ebillings);
    let modifiedInputErrors = inputErrors;
    let totalError = 0;

    for (let i = 0; i < ebillingIds.length; i++) {
      const id = ebillingIds[i];
      let exist = false;

      for (let j = 0; j < formRows.length; j++) {
        const row = formRows[j];
        if (`${row.id}` === `${id}`) {
          exist = true;
          break;
        }
      }

      if (!exist) continue;

      const ebilling = ebillings[id];
      modifiedInputErrors = { ...modifiedInputErrors, [id]: {} };

      if (!ebilling.kap?.id) {
        modifiedInputErrors = {
          ...modifiedInputErrors,
          [id]: {
            ...modifiedInputErrors[id],
            kap: t("Tax account code (KAP) is required."),
          },
        };
      }
      if (!ebilling.kjs?.id) {
        modifiedInputErrors = {
          ...modifiedInputErrors,
          [id]: {
            ...modifiedInputErrors[id],
            kjs: t("Tax payment code (KJS) is required."),
          },
        };
      }
      if (!ebilling.year) {
        modifiedInputErrors = {
          ...modifiedInputErrors,
          [id]: {
            ...modifiedInputErrors[id],
            year: t("Tax year is required."),
          },
        };
      }
      if (!ebilling.startPeriod) {
        modifiedInputErrors = {
          ...modifiedInputErrors,
          [id]: {
            ...modifiedInputErrors[id],
            startPeriod: t("Start period is required."),
          },
        };
      }
      if (!ebilling.endPeriod) {
        modifiedInputErrors = {
          ...modifiedInputErrors,
          [id]: {
            ...modifiedInputErrors[id],
            endPeriod: t("End period is required."),
          },
        };
      }
      if (!Boolean(consultationIdParam === consultation.id)) {
        if (!ebilling.npwp?.value) {
          modifiedInputErrors = {
            ...modifiedInputErrors,
            [id]: {
              ...modifiedInputErrors[id],
              npwp: t("NPWP is required."),
            },
          };
        } else {
          if (
            !Boolean(
              ebilling.npwp?.value.length >= 15 &&
              ebilling.npwp?.value.length <= 16,
            )
          ) {
            modifiedInputErrors = {
              ...modifiedInputErrors,
              [id]: {
                ...modifiedInputErrors[id],
                npwp: t("Invalid NPWP. Enter 15-16 digits."),
              },
            };
          }
        }
      }
      if (!ebilling.total?.value) {
        modifiedInputErrors = {
          ...modifiedInputErrors,
          [id]: {
            ...modifiedInputErrors[id],
            total: t("Total is required."),
          },
        };
      }
      if (!ebilling.email) {
        modifiedInputErrors = {
          ...modifiedInputErrors,
          [id]: {
            ...modifiedInputErrors[id],
            email: t("Email is required."),
          },
        };
      }
      if (ebilling.kjs?.hasSk && !ebilling.sk?.value) {
        modifiedInputErrors = {
          ...modifiedInputErrors,
          [id]: {
            ...modifiedInputErrors[id],
            sk: t("Notice (SK) number is required."),
          },
        };
      }
      if (!termsAgree) {
        modifiedInputErrors = {
          ...modifiedInputErrors,
          [id]: {
            ...modifiedInputErrors[id],
            termsAgree: t("Agreeing to the terms is required."),
          },
        };
      }
      if (
        !Boolean(consultationIdParam === consultation.id) &&
        ebilling.kjs?.hasOtherNpwp &&
        ebilling?.taxSubject === "other"
      ) {
        if (!ebilling.otherNpwp?.value) {
          modifiedInputErrors = {
            ...modifiedInputErrors,
            [id]: {
              ...modifiedInputErrors[id],
              otherNpwp: t("NPWP is required."),
            },
          };
        } else {
          if (
            !Boolean(
              ebilling.otherNpwp?.value.length >= 15 &&
              ebilling.otherNpwp?.value.length <= 16,
            )
          ) {
            modifiedInputErrors = {
              ...modifiedInputErrors,
              [id]: {
                ...modifiedInputErrors[id],
                otherNpwp: t("Invalid NPWP. Enter 15-16 digits."),
              },
            };
          }
        }
      }

      if (ebilling.kjs?.hasNop && !ebilling.nop?.value) {
        modifiedInputErrors = {
          ...modifiedInputErrors,
          [id]: {
            ...modifiedInputErrors[id],
            nop: t("Tax Object Number (NOP) is required."),
          },
        };
      }

      const formErrors = Object.keys(modifiedInputErrors[id]).length;
      if (formErrors > 0) {
        const modifiedRows = formRows.map((row) => {
          if (`${row.id}` === `${id}`) return { ...row, rowOpen: true };
          return row;
        });
        setFormRows(modifiedRows);
      }

      totalError += formErrors;
    }

    setinputErrors(modifiedInputErrors);
    return totalError === 0;
  };

  const onAddFormRow = () => {
    if (user.membership === "FREE") {
      setModal({
        visible: true,
        title: t("Premium Feature"),
        description: t(
          "Only premium member can use the mass e-billing feature.",
        ),
        type: "WARNING",
      });
      return;
    }

    if (verifyEbillingForm()) {
      if (formRows.length === 10) return;

      const newCount = rowCount + 1;
      const modifiedRows = formRows.map((row) => ({
        ...row,
        rowOpen: false,
      }));
      modifiedRows.push({ id: newCount, rowOpen: true });
      setFormRows(modifiedRows);
      setrowCount(newCount);

      let previousEbilling = {};
      const ebillingKeys = Object.keys(ebillings);
      for (let index = ebillingKeys.length - 1; index >= 0; index--) {
        const ebillingId = ebillingKeys[index];
        const ebilling = ebillings[ebillingId];
        if (ebilling.kap) {
          previousEbilling = ebilling;
          break;
        }
      }

      const newEbilling = {
        year: years[0],
        email: user.email,
        name: user.name,
      };

      if (config.kapKjs && previousEbilling.kap && previousEbilling.kjs) {
        newEbilling.kap = previousEbilling.kap;
        newEbilling.kjs = previousEbilling.kjs;
        if (previousEbilling.kjs.period === "ANNUALLY") {
          newEbilling.year = years[1];
          newEbilling.startPeriod = MONTHS[0];
          newEbilling.endPeriod = MONTHS[11];
        }
      }
      if (config.npwp && previousEbilling.npwp) {
        newEbilling.npwp = previousEbilling.npwp;
      }
      if (config.npwp && previousEbilling.email) {
        newEbilling.email = previousEbilling.email;
      }
      if (config.npwp && previousEbilling.name) {
        newEbilling.name = previousEbilling.name;
      }
      if (config.period && previousEbilling.year) {
        newEbilling.year = previousEbilling.year;
        if (previousEbilling.startPeriod) {
          newEbilling.startPeriod = previousEbilling.startPeriod;
        }
        if (previousEbilling.endPeriod) {
          newEbilling.endPeriod = previousEbilling.endPeriod;
        }
      }
      if (config.total && previousEbilling.total) {
        newEbilling.total = previousEbilling.total;
      }

      setebillings({
        ...ebillings,
        [newCount]: newEbilling,
      });
    }
  };

  const onDeleteFormRow = (id) => {
    if (formRows.length > 1) {
      let modifiedRows = formRows.filter((x) => x["id"] !== id);
      if (modifiedRows.length === 1) {
        modifiedRows[0] = { ...modifiedRows[0], rowOpen: true };
      }
      setFormRows(modifiedRows);
      setebillings({ ...ebillings, [id]: {} });
      setinputErrors({ ...inputErrors, [id]: {} });
    }
  };

  const toggleRow = (id) => {
    const modifiedRows = formRows.map((row) => {
      if (row.id === id) return { ...row, rowOpen: !row.rowOpen };
      return row;
    });
    setFormRows(modifiedRows);
  };

  const onChangeInput = (id, key, value) => {
    let additionalChange = {};

    if (key === "kap") {
      additionalChange = {
        ...additionalChange,
        kjs: null,
      };
    }

    if (key === "startPeriod" && ebillings[id]?.kjs?.period === "MONTHLY") {
      additionalChange = {
        ...additionalChange,
        endPeriod: value,
      };
    }

    if (key === "taxSubject" && value === "own") {
      additionalChange = {
        ...additionalChange,
        otherNpwp: "",
      };
    }

    if (key === "kjs") {
      if (value.hasOtherNpwp) {
        additionalChange = {
          ...additionalChange,
          taxSubject: "own",
        };
      }

      if (value.period === "ANNUALLY") {
        additionalChange = {
          ...additionalChange,
          year: years[1],
          startPeriod: MONTHS[0],
          endPeriod: MONTHS[11],
        };
      } else {
        additionalChange = {
          ...additionalChange,
          year: years[0],
          startPeriod: MONTHS[new Date().getMonth()],
          endPeriod: MONTHS[new Date().getMonth()],
        };
      }
    }

    setebillings({
      ...ebillings,
      [id]: {
        ...ebillings[id],
        [key]: value,
        ...additionalChange,
      },
    });
  };

  const transformEbilling = (ebilling) => {
    const inputEbilling = {
      kap: ebilling.kap.id,
      kjsId: ebilling.kjs.id,
      kjs: ebilling.kjs.kjsId,
      year: ebilling.year?.value,
      total: Number(ebilling.total?.value),
      npwp: ebilling.npwp?.value,
      email: ebilling.email,
    };
    if (ebilling.startPeriod?.value) {
      let startPeriod = ebilling.startPeriod.value;
      startPeriod = startPeriod < 10 ? `0${startPeriod}` : `${startPeriod}`;
      inputEbilling["startPeriod"] = startPeriod;
    }

    if (ebilling.startPeriod?.value) {
      let endPeriod = ebilling.endPeriod.value;
      endPeriod = endPeriod < 10 ? `0${endPeriod}` : `${endPeriod}`;
      inputEbilling["endPeriod"] = endPeriod;
    }

    if (ebilling.name) {
      inputEbilling["name"] = ebilling.name;
    }

    if (ebilling.note) {
      inputEbilling["note"] = ebilling.note;
    }

    if (ebilling.sk) {
      inputEbilling["sk"] = ebilling.sk.value;
    }

    if (ebilling.nop) {
      inputEbilling["nop"] = ebilling.nop.value;
    }

    if (ebilling.otherNpwp) {
      inputEbilling["collectorNpwp"] = ebilling.otherNpwp.value;
    }

    if (ebilling.selectedBusiness?.taxNumber) {
      delete inputEbilling.npwp;
      inputEbilling["businessID"] = ebilling.selectedBusiness.id;
    }

    return inputEbilling;
  };

  const onSubmit = (sendDraft = false) => {
    if (verifyEbillingForm()) {
      const keys = Object.keys(ebillings);
      let inputEbillings = [];
      keys.forEach((key) => {
        const ebilling = ebillings[key];
        if (Object.keys(ebilling).length === 0) {
          // skip
        } else {
          inputEbillings.push(ebilling);
        }
      });
      const items = inputEbillings.map((ebilling) =>
        transformEbilling(ebilling),
      );

      if (sendDraft) {
        const action = {
          target: "NONE",
          name: "EBILLING",
          params: JSON.stringify(items[0]),
        };
        const input = {
          id: consultation.id,
          userID: user.id,
          text: `${ebillings[1].kap.id} - ${ebillings[1].kap.name}`,
          action,
        };
        sendMessage(consultationDispatch, input);
      } else {
        requestCreateEbillingMass(dispatch, { items });
      }
    }
  };

  return (
    <div className="space-y-4">
      <ModalConfirm
        data-testid="success-modal"
        open={modal.visible}
        setOpen={(open) => {
          setModal((prevModal) => ({
            ...prevModal,
            visible: open,
          }));
        }}
        title={modal.title}
        description={modal.description}
        type={modal.type}
        onConfirm={navigateToEbilling}
      />
      <Modal
        title="E-Billing Terms"
        show={openModalTerms}
        onClose={() => setopenModalTerms(false)}
      >
        <div className="flex flex-col text-sm text-gray-700 p-4 gap-4">
          {EBILLING_TERMS.map((item, index) => (
            <div key={index} className="flex gap-4">
              <span>{index + 1}.</span>
              <span>{t(item.label)}</span>
            </div>
          ))}
        </div>
      </Modal>
      {consultationIdParam === consultation.id &&
        user.id &&
        user.membership === "CONSULTANT" && (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden md:w-max">
            <div className="flex flex-col divide-y divide-gray-200">
              <div className="p-4">
                <Alert
                  type="info"
                  message="Drafting an E-Billing for Consultation"
                />
              </div>
              <div className="flex flex-col p-4">
                <span className="text-sm font-medium text-gray-700">
                  {consultation?.description?.name}
                </span>
                <span className="text-sm text-gray-700">
                  {consultation?.category?.name}
                </span>
              </div>
            </div>
          </div>
        )}
      <div className="bg-white border border-gray-200 rounded-md sm:rounded-lg shadow-sm divide-y divide-gray-200">
        {user.membership === "FREE" && (
          <div className="py-4 px-4 sm:px-6">
            <div className="flex flex-col sm:flex-row space-y-2 justify-between sm:items-center">
              {loading ? (
                <div className="animate-pulse">
                  <div className="h-4 w-24 my-1 bg-gray-300 rounded-md"></div>
                  <div className="h-4 w-48 my-1 bg-gray-300 rounded-md"></div>
                </div>
              ) : (
                <div>
                  <Badge title={`${user.membership} MEMBER`} type="alert" />
                  <p className="font-medium text-gray-900 mt-1">
                    {t("E-Billing Quota Today")}: {quotalLeft}
                  </p>
                </div>
              )}
              <Button
                onClick={() => navigate("/subscription")}
                outline
                title="Become Premium Member"
              />
            </div>
          </div>
        )}
        {formRows.map((row, index) => (
          <Disclosure
            defaultOpen={true}
            as="div"
            key={row.id}
            className="py-4 px-4 sm:px-6"
          >
            {() => {
              // Label when row not active
              let minimizedLabel = ebillings[row.id]?.kap?.label;
              if (ebillings[row.id]?.year?.value) {
                if (
                  ebillings[row.id]?.startPeriod?.value &&
                  ebillings[row.id]?.endPeriod?.value
                ) {
                  if (
                    ebillings[row.id]?.startPeriod?.value ===
                    ebillings[row.id]?.endPeriod?.value
                  ) {
                    minimizedLabel = `${minimizedLabel} (${
                      ebillings[row.id]?.startPeriod?.value
                    } / ${ebillings[row.id]?.year?.value})`;
                  } else {
                    minimizedLabel = `${minimizedLabel} (${
                      ebillings[row.id]?.startPeriod?.value
                    }-${ebillings[row.id]?.endPeriod?.value} / ${
                      ebillings[row.id]?.year?.value
                    })`;
                  }
                } else {
                  minimizedLabel = `${minimizedLabel} (${
                    ebillings[row.id]?.year?.value
                  })`;
                }
              }

              // Set KJS list based on row selected kap
              let kjsItems = [];
              if (ebillings[row.id].kap?.kjs?.items) {
                kjsItems = ebillings[row.id].kap.kjs.items.map((item) => ({
                  ...item,
                  label: `${item.kjsId} - ${item.name}`,
                  value: item.id,
                }));
                kjsItems = kjsItems.sort((a, b) => a.kjsId - b.kjsId);
              }

              const selectedBusiness = ebillings[row.id]?.selectedBusiness;
              return (
                <>
                  {formRows.length > 1 ? (
                    <dt className="text-lg">
                      <DisclosureButton
                        as="div"
                        className="text-left w-full flex justify-between items-start text-gray-400"
                      >
                        <div className="block sm:inline-flex justify-start items-center gap-4 divide-x-0 sm:divide-x divide-gray-200">
                          <span className="w-full sm:w-auto font-medium text-gray-900">
                            E-Billing #{index + 1}
                          </span>
                          {row.rowOpen ? null : (
                            <div className="w-full sm:w-auto text-sm px-0 sm:px-4 text-gray-900">
                              <div>{minimizedLabel}</div>
                              <div>
                                {ebillings[row.id]?.total?.formattedValue}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="ml-6 gap-4 inline-flex justify-end">
                          <span className="h-7 flex items-center">
                            <button onClick={() => onDeleteFormRow(row.id)}>
                              <TrashIcon
                                className="h-6 w-6"
                                aria-hidden="true"
                              />
                            </button>
                          </span>
                          <span className="h-7 flex items-center">
                            <button onClick={() => toggleRow(row.id)}>
                              <ChevronDownIcon
                                className={classNames(
                                  row.rowOpen ? "-rotate-180" : "rotate-0",
                                  "h-6 w-6 transform",
                                )}
                                aria-hidden="true"
                              />
                            </button>
                          </span>
                        </div>
                      </DisclosureButton>
                    </dt>
                  ) : null}
                  {row.rowOpen ? (
                    <DisclosurePanel as="dd" className="mt-2 pr-10" static>
                      <div className="space-y-6 lg:col-span-9">
                        <div className="grid grid-cols-4 gap-6">
                          {loading ? (
                            <div className="col-span-4 sm:col-span-2 animate-pulse">
                              <div className="h-3 w-24 my-1 bg-gray-300 rounded-md"></div>
                              <div className="h-9 w-full my-2 bg-gray-300 rounded-md"></div>
                            </div>
                          ) : (
                            <div className="col-span-4 sm:col-span-2">
                              <FormLabel
                                htmlFor="kap"
                                label={t("Tax Account Code (KAP)")}
                                required
                              />
                              <Select
                                name="kap"
                                id="kap"
                                className="mt-1 text-sm"
                                classNamePrefix="select2-selection"
                                options={kapItems}
                                placeholder={t("Select Tax Account Code (KAP)")}
                                value={ebillings[row.id]?.kap}
                                onChange={(value) => {
                                  onChangeInput(row.id, "kap", value);
                                }}
                              />
                              {inputErrors[row.id]?.kap ? (
                                <p
                                  className="mt-2 text-sm text-red-600"
                                  id="kap-error"
                                >
                                  {t(inputErrors[row.id].kap)}
                                </p>
                              ) : null}
                            </div>
                          )}

                          <div className="col-span-4 sm:col-span-2">
                            <FormLabel
                              htmlFor="kjs"
                              label={t("Tax Payment Code (KJS)")}
                              required
                            />
                            <Select
                              name="kjs"
                              id="kjs"
                              className="mt-1 text-sm"
                              classNamePrefix="select2-selection"
                              options={kjsItems}
                              isDisabled={!ebillings[row.id]?.kap}
                              placeholder={
                                !ebillings[row.id]?.kap
                                  ? t("Please select KAP first")
                                  : t("Select Tax Payment Code (KJS)")
                              }
                              value={ebillings[row.id]?.kjs}
                              onChange={(value) => {
                                onChangeInput(row.id, "kjs", value);
                              }}
                            />
                            {inputErrors[row.id]?.kjs ? (
                              <p
                                className="mt-2 text-sm text-red-600"
                                id="kjs-error"
                              >
                                {t(inputErrors[row.id].kjs)}
                              </p>
                            ) : null}
                          </div>

                          <div className="col-span-4 sm:col-span-2">
                            <FormLabel
                              htmlFor="year"
                              label={t("Tax Year")}
                              required
                            />
                            <Select
                              name="year"
                              className="mt-1 text-sm"
                              classNamePrefix="select2-selection"
                              options={years}
                              placeholder={t("Select tax year")}
                              value={ebillings[row.id]?.year}
                              onChange={(value) => {
                                onChangeInput(row.id, "year", value);
                              }}
                            />
                            <a
                              href="https://coretaxdjp.pajak.go.id/"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                            >
                              {t("create2025BillingHint")}
                              <ExternalLinkIcon className="h-4 w-4" />
                            </a>
                            {inputErrors[row.id]?.year ? (
                              <p
                                className="mt-2 text-sm text-red-600"
                                id="year-error"
                              >
                                {t(inputErrors[row.id].year)}
                              </p>
                            ) : null}
                          </div>

                          <div className="col-span-4 xs:col-span-2 sm:col-span-1">
                            <FormLabel
                              htmlFor="startPeriod"
                              label={t("Start Period")}
                              required
                            />
                            <Select
                              id="start-periode-select"
                              name="startPeriod"
                              className="mt-1 text-sm"
                              classNamePrefix="select2-selection"
                              options={translatedMonths}
                              isDisabled={
                                !ebillings[row.id]?.kjs ||
                                ebillings[row.id]?.kjs?.period === "ANNUALLY"
                              }
                              placeholder={
                                !ebillings[row.id]?.kjs
                                  ? t("Please select KJS first")
                                  : t("Select start period")
                              }
                              value={ebillings[row.id]?.startPeriod}
                              onChange={(value) => {
                                onChangeInput(row.id, "startPeriod", value);
                              }}
                            />
                            {inputErrors[row.id]?.startPeriod ? (
                              <p
                                className="mt-2 text-sm text-red-600"
                                id="startPeriod-error"
                              >
                                {t(inputErrors[row.id].startPeriod)}
                              </p>
                            ) : null}
                          </div>

                          <div className="col-span-4 xs:col-span-2 sm:col-span-1">
                            <FormLabel
                              htmlFor="endPeriod"
                              label={t("End Period")}
                              required
                            />
                            <Select
                              name="endPeriod"
                              className="mt-1 text-sm"
                              classNamePrefix="select2-selection"
                              options={translatedMonths}
                              isDisabled={
                                !ebillings[row.id]?.kjs ||
                                ebillings[row.id]?.kjs?.period === "ANNUALLY" ||
                                ebillings[row.id]?.kjs?.period === "MONTHLY"
                              }
                              placeholder={
                                !ebillings[row.id]?.kjs
                                  ? t("Please select KJS first")
                                  : t("Select end period")
                              }
                              value={ebillings[row.id]?.endPeriod}
                              onChange={(value) => {
                                onChangeInput(row.id, "endPeriod", value);
                              }}
                            />
                            {inputErrors[row.id]?.endPeriod ? (
                              <p
                                className="mt-2 text-sm text-red-600"
                                id="endPeriod-error"
                              >
                                {t(inputErrors[row.id].endPeriod)}
                              </p>
                            ) : null}
                          </div>

                          <div className="col-span-4 sm:col-span-2">
                            <FormLabel
                              htmlFor="businessID"
                              label={t("businessProfile")}
                            />
                            {businessError ? (
                              <ErrorSelect
                                name="tax"
                                customClassNames="mt-1"
                                error={businessError}
                                onRefresh={() => {
                                  requestListBusiness(businessDispatch, {
                                    owner: user.id,
                                  });
                                }}
                              />
                            ) : (
                              <Select
                                id="business-profile-select"
                                className="mt-1 text-sm"
                                classNamePrefix="select2-selection"
                                options={businesses}
                                placeholder={
                                  businesses.length === 0
                                    ? t("No Business Profile Yet")
                                    : t("Select Business Name")
                                }
                                isLoading={businessLoading}
                                isDisabled={businesses.length === 0}
                                isClearable
                                value={ebillings[row.id]?.selectedBusiness}
                                onChange={(value) => {
                                  onChangeInput(
                                    row.id,
                                    "selectedBusiness",
                                    value,
                                  );
                                }}
                              />
                            )}

                            {selectedBusinessError[row.id] ? (
                              <p
                                className="mt-2 text-sm text-red-600"
                                id="selectedBusiness-error"
                              >
                                {t(selectedBusinessError[row.id])}
                              </p>
                            ) : null}
                          </div>

                          <div className="col-span-4 sm:col-span-2">
                            <FormLabel
                              htmlFor="npwp"
                              label={t("Tax Identification Number (NPWP)")}
                              required={
                                !Boolean(
                                  consultationIdParam === consultation.id,
                                )
                              }
                            />
                            <NumericFormat
                              data-testid="npwp-input"
                              type="text"
                              name="npwp"
                              id="npwp"
                              allowLeadingZeros
                              className={classNames(
                                selectedBusiness?.taxNumber &&
                                  "bg-gray-100 text-gray-500",
                                "mt-1 py-2 px-3 ebilling-input text-sm",
                              )}
                              placeholder={t(
                                "Enter the tax identification number (NPWP)",
                              )}
                              displayType={
                                selectedBusiness?.taxNumber ? "text" : "input"
                              }
                              value={ebillings[row.id]?.npwp?.value || ""}
                              onValueChange={(value) => {
                                onChangeInput(row.id, "npwp", value);
                              }}
                              isAllowed={({ value }) => value.length <= 16}
                            />
                            {inputErrors[row.id]?.npwp ? (
                              <p
                                className="mt-2 text-sm text-red-600"
                                id="npwp-error"
                              >
                                {inputErrors[row.id].npwp}
                              </p>
                            ) : null}
                          </div>

                          {/* SK */}
                          {ebillings[row.id]?.kjs?.hasSk && (
                            <div className="col-span-4 sm:col-span-2">
                              <FormLabel
                                htmlFor="sk"
                                label={t("Notice (SK) Number")}
                                required
                              />
                              <PatternFormat
                                type="text"
                                name="sk"
                                id="sk"
                                className="mt-1 py-2 px-3 ebilling-input text-sm"
                                placeholder={t("Enter notice (SK) number")}
                                format="#####/###/##/###/##"
                                mask="_"
                                value={ebillings[row.id]?.sk?.value || ""}
                                onValueChange={(value) => {
                                  onChangeInput(row.id, "sk", value);
                                }}
                              />
                              {inputErrors[row.id]?.sk ? (
                                <p
                                  className="mt-2 text-sm text-red-600"
                                  id="sk-error"
                                >
                                  {t(inputErrors[row.id].sk)}
                                </p>
                              ) : null}
                            </div>
                          )}

                          {/* Tax Object */}
                          {ebillings[row.id]?.kjs?.hasNop && (
                            <div className="col-span-4 sm:col-span-2">
                              <FormLabel
                                htmlFor="nop"
                                label={t("Tax Object Number (NOP)")}
                                required
                              />
                              <PatternFormat
                                type="text"
                                name="nop"
                                id="nop"
                                className="mt-1 py-2 px-3 ebilling-input text-sm"
                                placeholder={t(
                                  "Enter the tax object number (NOP)",
                                )}
                                format="##.##.###.###.###.####.#"
                                mask="_"
                                value={ebillings[row.id]?.nop?.value || ""}
                                onValueChange={(value) => {
                                  onChangeInput(row.id, "nop", value);
                                }}
                              />
                              {inputErrors[row.id]?.nop ? (
                                <p
                                  className="mt-2 text-sm text-red-600"
                                  id="nop-error"
                                >
                                  {inputErrors[row.id].nop}
                                </p>
                              ) : null}
                            </div>
                          )}

                          {/* Subjek Pajak */}
                          {ebillings[row.id]?.kjs?.hasOtherNpwp && (
                            <div className="col-span-4 sm:col-span-2">
                              <label
                                htmlFor="tax-subject"
                                className="block text-sm font-medium text-gray-700"
                              >
                                {t("Tax Subject")}
                              </label>
                              <div className="space-y-4 sm:flex sm:items-center sm:space-y-0 sm:space-x-10 mt-1 py-2">
                                <div className="flex items-center">
                                  <input
                                    id="own"
                                    name="tax-subject"
                                    type="radio"
                                    value="own"
                                    checked={
                                      ebillings[row.id]?.taxSubject === "own" ||
                                      !Boolean(ebillings[row.id]?.otherNpwp)
                                    }
                                    onChange={(event) => {
                                      onChangeInput(
                                        row.id,
                                        "taxSubject",
                                        event.target.value,
                                      );
                                    }}
                                    className="text-sm focus:outline-none focus:ring-0 focus:ring-offset-0 h-4 w-4 text-blue-600 border-gray-300"
                                  />
                                  <label
                                    htmlFor="own"
                                    className="ml-3 block text-sm font-medium text-gray-700"
                                  >
                                    {t("Own NPWP")}
                                  </label>
                                </div>
                                <div className="flex items-center">
                                  <input
                                    id="other"
                                    name="tax-subject"
                                    type="radio"
                                    value={"other"}
                                    checked={
                                      ebillings[row.id]?.taxSubject === "other"
                                    }
                                    onChange={(event) => {
                                      onChangeInput(
                                        row.id,
                                        "taxSubject",
                                        event.target.value,
                                      );
                                    }}
                                    className="text-sm focus:outline-none focus:ring-0 focus:ring-offset-0 h-4 w-4 text-blue-600 border-gray-300"
                                  />
                                  <label
                                    htmlFor="other"
                                    className="ml-3 block text-sm font-medium text-gray-700"
                                  >
                                    {t("Other NPWP")}
                                  </label>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* NPWP Lain Input */}
                          {ebillings[row.id]?.kjs?.hasOtherNpwp &&
                            ebillings[row.id]?.taxSubject === "other" && (
                              <div className="col-span-4 sm:col-span-2">
                                <FormLabel
                                  htmlFor="other-npwp"
                                  label={t("Other NPWP")}
                                  required
                                />
                                <NumericFormat
                                  type="text"
                                  name="other-npwp"
                                  id="other-npwp"
                                  allowLeadingZeros
                                  className="mt-1 py-2 px-3 ebilling-input text-sm"
                                  placeholder={t("Enter the other NPWP")}
                                  displayType="input"
                                  value={
                                    ebillings[row.id]?.otherNpwp?.value || ""
                                  }
                                  onValueChange={(value) => {
                                    onChangeInput(row.id, "otherNpwp", value);
                                  }}
                                  isAllowed={({ value }) => value.length <= 16}
                                />
                                {inputErrors[row.id]?.otherNpwp ? (
                                  <p
                                    className="mt-2 text-sm text-red-600"
                                    id="otherNpwp-error"
                                  >
                                    {t(inputErrors[row.id].otherNpwp)}
                                  </p>
                                ) : null}
                              </div>
                            )}

                          <div className="col-span-4 sm:col-span-2">
                            <FormLabel
                              htmlFor="total"
                              label={t("Total Payment")}
                              required
                            />
                            <NumericFormat
                              data-testid="total-payment-input"
                              type="text"
                              name="total"
                              id="total"
                              className="mt-1 py-2 px-3 ebilling-input text-sm"
                              placeholder={t("Enter total payment")}
                              thousandSeparator="."
                              decimalSeparator=","
                              displayType="input"
                              prefix={"Rp"}
                              value={ebillings[row.id]?.total?.value || ""}
                              onValueChange={(value) => {
                                onChangeInput(row.id, "total", value);
                              }}
                            />
                            {inputErrors[row.id]?.total ? (
                              <p
                                className="mt-2 text-sm text-red-600"
                                id="total-error"
                              >
                                {t(inputErrors[row.id].total) || ""}
                              </p>
                            ) : null}
                          </div>

                          <div className="col-span-4 sm:col-span-2">
                            <FormLabel htmlFor="name" label={t("Name")} />
                            <input
                              type="text"
                              name="name"
                              id="name"
                              className="mt-1 py-2 px-3 ebilling-input text-sm"
                              placeholder={t("Enter name of the NPWP owner")}
                              value={ebillings[row.id]?.name || ""}
                              onChange={(event) => {
                                onChangeInput(
                                  row.id,
                                  "name",
                                  event.target.value,
                                );
                              }}
                            />
                          </div>

                          <div className="col-span-4 sm:col-span-2">
                            <FormLabel htmlFor="note" label={t("Note")} />
                            <input
                              type="text"
                              name="note"
                              id="note"
                              className="mt-1 py-2 px-3 ebilling-input text-sm"
                              placeholder={t("Enter note")}
                              value={ebillings[row.id]?.note || ""}
                              onChange={(event) => {
                                onChangeInput(
                                  row.id,
                                  "note",
                                  event.target.value,
                                );
                              }}
                            />
                          </div>

                          <div className="col-span-4 sm:col-span-2">
                            <FormLabel
                              htmlFor="email"
                              label={t("Email")}
                              required
                            />
                            <input
                              type="email"
                              name="email"
                              id="email"
                              className="mt-1 py-2 px-3 ebilling-input text-sm"
                              placeholder={t(
                                "Enter email to receive payment receipt",
                              )}
                              value={ebillings[row.id]?.email || ""}
                              onChange={(event) => {
                                onChangeInput(
                                  row.id,
                                  "email",
                                  event.target.value,
                                );
                              }}
                            />
                            {inputErrors[row.id]?.email ? (
                              <p
                                className="mt-2 text-sm text-red-600"
                                id="email-error"
                              >
                                {t(inputErrors[row.id].email)}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </DisclosurePanel>
                  ) : null}
                </>
              );
            }}
          </Disclosure>
        ))}
        <div className="py-4 px-4 sm:px-6">
          <div className="flex items-center">
            <input
              name="sourceIncome"
              type="checkbox"
              checked={termsAgree}
              onChange={() => {
                settermsAgree(!termsAgree);
              }}
              className="focus:ring-blue-500 h-4 w-4 text-blue-500 border-gray-300"
            />
            <div className="flex ml-3 text-sm font-medium gap-1">
              <label className=" text-gray-700">{t("Agree")}</label>
              <button
                onClick={() => setopenModalTerms(true)}
                className=" text-blue-600"
              >
                {t("E-Billing Terms")}
              </button>
            </div>
          </div>
          {inputErrors[1]?.termsAgree && (
            <p className="mt-2 text-sm text-red-600" id="kap-error">
              {t(inputErrors[1].termsAgree)}
            </p>
          )}
        </div>

        <div
          className={`flex-1 flex flex-wrap items-center ${
            consultationIdParam === consultation.id
              ? "justify-end"
              : "justify-between"
          } py-4 px-4 sm:px-6 gap-4`}
        >
          {!Boolean(consultationIdParam === consultation.id) && (
            <div className="relative z-0 inline-flex w-full sm:w-auto shadow-sm rounded-md">
              <button
                type="button"
                onClick={onAddFormRow}
                className="relative inline-flex items-center w-full sm:w-auto px-4 py-3 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              >
                {t("Add Another")}
              </button>
              <Popover className="-ml-px inline-flex">
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <PopoverPanel className="absolute -translate-y-full -top-2 right-0 sm:left-0 mb-2 py-1 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
                    {configItems.map((item) => (
                      <div
                        key={item.id}
                        className="relative flex items-start px-4 py-2"
                      >
                        <div className="flex items-center h-5">
                          <input
                            id={item.key}
                            aria-describedby="comments-description"
                            name="config"
                            type="checkbox"
                            className="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded"
                            checked={config[item.id]}
                            onChange={(event) =>
                              setconfig({
                                ...config,
                                [item.id]: event.target.checked,
                              })
                            }
                          />
                        </div>
                        <div className="ml-3 text-sm">
                          <label
                            htmlFor={item.key}
                            className="text-sm text-gray-700"
                          >
                            {t(item.name)}
                          </label>
                        </div>
                      </div>
                    ))}
                  </PopoverPanel>
                </Transition>
                <PopoverButton className="inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500">
                  <span className="sr-only">Open options</span>
                  <CountBadge count={checkedConfig} color="blue" />
                  <CogIcon className="h-6 w-6 ml-1" aria-hidden="true" />
                </PopoverButton>
              </Popover>
            </div>
          )}
          {user.membership === "FREE" && quotalLeft === 0 ? (
            <Button
              type="button"
              className="w-full sm:w-auto"
              disabled
              title="No Quota Left"
            />
          ) : (
            <Button
              data-testid="ebilling-create-button"
              type="button"
              className="w-full sm:w-auto"
              onClick={() => {
                if (consultationIdParam === consultation.id) {
                  onSubmit(true);
                } else {
                  onSubmit();
                }
              }}
              disabled={loading || sendLoading}
              loading={loading || sendLoading}
              title={`${t("Create")}${
                formRows.length && ` ${formRows.length}`
              } E-Billing`}
            />
          )}
        </div>
      </div>
      <div className="mt-4 bg-white border border-gray-200 py-4 px-4 sm:px-6 rounded-md sm:rounded-lg shadow">
        <Partner
          title="Collaborating with BRI"
          description="Official PJAP Directorate General of Taxes"
          url="https://pajak.go.id/id/pt-bank-rakyat-indonesia-persero-tbk"
        />
      </div>
    </div>
  );
};

export default EbillingCreate;
